/**
 * @file layer-panel.ts
 * @description Handles the DOM rendering and user interaction for the Layers panel.
 */

import { g, layers, LayerPropertyAction } from '@hcie/core';
import { 
    toggleLayerVisibility, 
    selectLayer, 
    renameLayer, 
    moveLayerUp, 
    moveLayerDown, 
    addLayer, 
    deleteLayer 
} from '../layer-service';
import { renderLayers } from '../layers';

/**
 * @function updateLayerPanelUI
 * @description Refreshes the global layers panel by regenerating the DOM elements for the layer list.
 * @returns {void}
 */
export function updateLayerPanelUI(): void {
    // [CRITICAL_LOGIC_START]
    const layersList = document.querySelector('#layers-pane .layers-list');
    if (!layersList) return;

    layersList.innerHTML = '';

    // Render layers in reverse order (top layer first in the panel)
    for (let index = layers.length - 1; index >= 0; index--) {
        const layer = layers[index];
        const layerItem = createLayerItem(layer, index);
        layersList.appendChild(layerItem);
    }

    // Sync opacity slider state
    const opacitySlider = document.getElementById('layerOpacity') as HTMLInputElement | null;
    if (opacitySlider) {
        opacitySlider.value = Math.round(layers[g.activeLayerIndex].opacity * 100).toString();
    }
    // [CRITICAL_LOGIC_END]
}

/**
 * @function createLayerItem
 * @description Factory function to create a single layer item row in the panel.
 * @private
 */
function createLayerItem(layer: any, index: number): HTMLElement {
    const item = document.createElement('div');
    item.className = `layer-item${index === g.activeLayerIndex ? ' selected' : ''}`;
    item.dataset.index = index.toString();

    // 1. Visibility Toggle
    const visibilityIcon = document.createElement('span');
    visibilityIcon.className = `layer-icon${layer.visible ? ' active' : ''}`;
    visibilityIcon.textContent = layer.visible ? '👁' : '·';
    visibilityIcon.addEventListener('click', (event) => {
        event.stopPropagation();
        toggleLayerVisibility(index);
    });

    // 2. Thumbnail Preview
    const thumbnail = document.createElement('div');
    thumbnail.className = 'layer-thumbnail';
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = 32;
    thumbCanvas.height = 32;
    const thumbCtx = thumbCanvas.getContext('2d');
    if (thumbCtx) {
        thumbCtx.drawImage(layer.canvas as HTMLCanvasElement, 0, 0, g.image_width, g.image_height, 0, 0, 32, 32);
    }
    thumbnail.appendChild(thumbCanvas);

    // 3. Name Display and Renaming
    const nameLabel = document.createElement('div');
    nameLabel.className = 'layer-name';
    nameLabel.textContent = layer.name;
    nameLabel.addEventListener('dblclick', (event) => handleLayerRenameUI(event, layer, index, nameLabel));

    // 4. Blend Mode Selector
    const blendSelector = createBlendModeSelector(layer, index);
    
    // 5. Interaction
    item.addEventListener('click', () => selectLayer(index));
    
    item.append(visibilityIcon, thumbnail, nameLabel, blendSelector);
    return item;
}

/**
 * @function handleLayerRenameUI
 * @description Swaps the name label with an input field for inline renaming.
 * @private
 */
function handleLayerRenameUI(event: MouseEvent, layer: any, index: number, nameLabel: HTMLElement): void {
    event.stopPropagation();
    const input = document.createElement('input');
    input.type = 'text';
    input.value = layer.name;
    input.className = 'layer-name-input';
    
    nameLabel.replaceWith(input);
    input.focus();
    input.select();
    
    const applyRename = () => renameLayer(index, input.value || layer.name);
    
    input.addEventListener('blur', applyRename);
    input.addEventListener('keydown', (ke) => {
        if (ke.key === 'Enter') applyRename();
        if (ke.key === 'Escape') {
            input.value = layer.name;
            applyRename();
        }
    });
}

/**
 * @function createBlendModeSelector
 * @description Generates a dropdown for layer blending modes.
 * @private
 */
function createBlendModeSelector(layer: any, index: number): HTMLSelectElement {
    const selector = document.createElement('select');
    selector.className = 'layer-blend-mode';
    
    const blendModes: readonly string[] = [
        'source-over', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
        'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference',
        'exclusion', 'hue', 'saturation', 'color', 'luminosity'
    ];

    blendModes.forEach(mode => {
        const option = document.createElement('option');
        option.value = mode;
        option.textContent = mode.charAt(0).toUpperCase() + mode.slice(1).replace('-', ' ');
        if (layer.blendMode === mode) option.selected = true;
        selector.appendChild(option);
    });

    selector.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        (selector as any).dataset.initialValue = layers[index].blendMode;
    });
    
    selector.addEventListener('change', (e) => {
        const nextValue = (e.target as HTMLSelectElement).value;
        const initialValue = (selector as any).dataset.initialValue || layers[index].blendMode;
        
        if (window.historyManager && initialValue !== nextValue) {
            window.historyManager.push(new LayerPropertyAction(index, 'blendMode', initialValue as any, nextValue as any));
        }
        layers[index].blendMode = nextValue as any;
        renderLayers();
    });

    return selector;
}

/**
 * @function initLayerPanelEvents
 * @description Binds structural layer operations to panel footer buttons.
 * @returns {void}
 */
export function initLayerPanelEvents(): void {
    const btnAdd = document.querySelector('.panel-footer-btn[title="Add Layer"]');
    const btnDelete = document.querySelector('.panel-footer-btn[title="Delete Layer"]');
    const btnUp = document.querySelector('.panel-footer-btn[title="Move Up"]');
    const btnDown = document.querySelector('.panel-footer-btn[title="Move Down"]');

    btnAdd?.addEventListener('click', () => addLayer());
    btnDelete?.addEventListener('click', () => deleteLayer());
    btnUp?.addEventListener('click', () => moveLayerUp());
    btnDown?.addEventListener('click', () => moveLayerDown());

    // Opacity Slider Initialization
    const opacitySlider = document.getElementById('layerOpacity') as HTMLInputElement | null;
    if (opacitySlider) {
        opacitySlider.addEventListener('input', (e) => {
            const val = parseInt((e.target as HTMLInputElement).value);
            const activeLayer = layers[g.activeLayerIndex];
            if (activeLayer) {
                activeLayer.opacity = val / 100;
                renderLayers();
            }
        });

        // History recording for opacity
        let startOpacity = 1.0;
        opacitySlider.addEventListener('mousedown', () => {
            startOpacity = layers[g.activeLayerIndex]?.opacity || 1.0;
        });

        opacitySlider.addEventListener('change', () => {
            if (window.historyManager) {
                window.historyManager.push(new LayerPropertyAction(g.activeLayerIndex, 'opacity', startOpacity, layers[g.activeLayerIndex].opacity));
            }
        });
    }
}
